// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import type { StateType, DispatchType } from "../../../global/types/redux";
import type { ErrorsType } from "../../types/UserFormType";
import {
  StyledTextField,
  CancelButton,
  SubmitButton,
  SingleCheckBox
} from "imagus-components";
import { ErrosDisplay, setErrorMsg, checkEmail } from "../ErrosDisplay";
import {
  GraphqlLineBreak,
  utf8OutRangeFilter
} from "../../../global/functions/MultilLineStrings";
import {
  fetchLicenceCompanyDetail,
  emailLicenceFile
} from "../../../admin/actions/licenceAdminActions";
import { buildLicenceItemJson } from "../../components/companies/CompileCompanytoJson";

import Grid from "@material-ui/core/Grid";

const DEBOUNCETIME = 500;

type EmailLicenceFormStatesType = {
  errors: ErrorsType,
  email: string,
  title: string,
  name: string,
  content: string,
  loading: boolean,
  isOld: boolean
};

type EmailLicenceFormPropsType = {
  licenceId: string,
  hostId: string,
  onClose: Function,
  actions: { [key: string]: Function }
};

const initialform: EmailLicenceFormStatesType = {
  errors: {},
  email: "",
  title: "",
  name: "",
  content: "",
  loading: false,
  isOld: false
};

function generateEmailContent(name: string): string {
  const body = `Dear ${name}\n\nPlease find attached the Imagus Software licence key file as requested.\n\nIf there is a problem with the licence key file we sent you, please reply to this email, and we'll help fix the problem.\n\nWith kind regards,\n\n`;
  return body;
}

class EmailLicenceForm extends Component<
  EmailLicenceFormPropsType,
  EmailLicenceFormStatesType
> {
  timeout: number;
  constructor(props: EmailLicenceFormPropsType) {
    super(props);
    this.state = initialform;
  }

  componentDidMount() {
    this.props.actions.fetchLicenceCompanyDetail(
      this.props.licenceId,
      this.setInitial
    );
  }

  setInitial = (node: *): * => {
    let initials = { ...initialform };
    initials.email = node.company.contactEmail || "";
    initials.name = node.company.contactName || "";
    initials.content = generateEmailContent(node.company.contactName);

    const licence = buildLicenceItemJson(node);
    const expiryDate = licence.expiryDate.format("DD/MMM/YYYY");
    let title = "";
    if (licence.licenceType === "DEMO") {
      title = "30-day Imagus Software Demo Licence - Expires on" + expiryDate;
    } else if (licence.licenceType === "EVALUATION") {
      title = "Imagus Software Evaluation  Licence - Expires on " + expiryDate;
    } else if (licence.licenceType === "LONGTERM") {
      title = "Imagus Software Long-Term Licence - Perpetual";
    } else if (licence.licenceType === "SHORTTERM") {
      title = "Imagus Software Short-Term Licence - Expires on " + expiryDate;
    }
    initials.title = title;

    this.setState({ ...initials, errors: {} });
  };

  handleTextChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.setState((state: EmailLicenceFormStatesType) => ({
      [name]: value
    }));

    if (name === "email") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(
        function (val: string) {
          this.setState({ errors: checkEmail(this.state.errors, val) });
        }.bind(this, value),
        DEBOUNCETIME
      );
    }
  };

  handleCheckBoxChange = (e: Event): * => {
    this.setState({
      [e.target.name]: !this.state[e.target.name]
    });
  };

  submit = (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((): * => ({ loading: true }));

    this.props.actions.emailLicenceFile(
      this.props.hostId,
      this.props.licenceId,
      this.state.email,
      this.state.name.trim(),
      this.state.title.trim(),
      GraphqlLineBreak(utf8OutRangeFilter(this.state.content), true),
      this.state.isOld,
      (): * => this.props.onClose(),
      this.submitFail
    );
  };

  submitFail = (errors: ErrorsType): * => {
    this.setState({
      errors: errors,
      loading: false
    });
  };

  render(): React$Element<*> {
    const errors = this.state.errors;
    const loading = this.state.loading;
    return (
      <form onSubmit={(!loading && this.submit) || undefined}>
        <Grid container spacing={8}>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Device / Host ID"
              name="hostId"
              value={this.props.hostId}
              onChange={null}
              inpuType="text"
              isDisabled
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Recipient"
              name="name"
              value={this.state.name}
              onChange={this.handleTextChange}
              inpuType="text"
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Title"
              name="title"
              value={this.state.title}
              onChange={this.handleTextChange}
              inpuType="text"
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Email"
              name="email"
              value={this.state.email}
              onChange={this.handleTextChange}
              inpuType="text"
              isInvalid={errors.email !== undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={false}
              label="Email Content"
              name="content"
              value={this.state.content}
              onChange={this.handleTextChange}
              inpuType="text"
              rows={8}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SingleCheckBox
              name="isOld"
              value={this.state.isOld}
              onChange={this.handleCheckBoxChange}
              label="Generate Old key Licence file?"
            />
          </Grid>
          <Grid item xs={12}>
            <ErrosDisplay errors={errors} form="email licence" />
          </Grid>
          <Grid item xs={12}>
            <div className="form-right-btn-block">
              <CancelButton onClick={this.props.onClose} />
              <SubmitButton
                loading={loading}
                disabled={Object.keys(errors).length > 0}
              />
            </div>
          </Grid>
        </Grid>
      </form>
    );
  }
}

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators(
    { fetchLicenceCompanyDetail, emailLicenceFile },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(EmailLicenceForm);
